import React from 'react';
import PropTypes from 'prop-types';
import Link from './Link';
import { generateDatas, revealable } from './utils';
import { Popover } from 'antd';

/**
 * MultiDataColumn — a two-line table cell (main value above, sub value below).
 *
 * NO POPOVER. It used to wrap the cell in an antd `Popover` behind a `pop` prop,
 * which rendered a large white card with an arrow repeating BOTH lines, anchored
 * to the wrapping <div> so it covered the column header above it. 98d225eadcc
 * swapped it for a native `title` per line, which cannot be selected or copied;
 * utils.js now wraps each line in `CopyableValue` (FIN-14012) — one selectable
 * tooltip per value, anchored below the text so it never covers the header.
 *
 * `pop` is gone rather than ignored: a silently-accepted prop invites someone to
 * pass it and wonder why nothing happens.
 */
const MultiDataColumn = ({ children, data = [], combined = false }) => {
  /* `combined` REVEALS BOTH LINES IN ONE TOOLTIP instead of one per line.
     Opt-in, for cells whose two lines are read together — Party ID with Party
     Name, where the pairing is the point and dev-1 showed them together. The
     per-line default stays everywhere else, where a tooltip per value is what
     makes each one copyable on its own. */
  const content = generateDatas(data, !combined);

  const cell = (
    <div>
      {content}
      {children}
    </div>
  );

  /* AN ANTD POPOVER, THE SAME COMPONENT 6.3.4.1 USED — not a Tooltip styled to
     imitate one. dev-1 is the reference this ticket compares against, and it
     renders a Popover whose content is the SAME `MainData` / `SubData` lines the
     cell itself uses.

     That is where the look comes from, and why it matches by construction: the
     white card, its padding, radius, shadow and the WHITE arrow are all Popover
     defaults, and the two lines keep the column's own type and colour (#31394d
     at weight 500 over #748aa1 at weight 400). Matching the same result with a
     Tooltip meant hand-setting five values and theming the arrow separately —
     a Tooltip's arrow is dark by default and takes its colour from a CSS custom
     property, which caught us three times.

     Popover content stays open while the pointer is inside it, so the value is
     still selectable — the property CopyableValue was added for. */
  return combined ? (
    <Popover content={<div>{generateDatas(revealable(data), false)}</div>}>
      {cell}
    </Popover>
  ) : (
    cell
  );
};

MultiDataColumn.propTypes = {
  children: PropTypes.node,
  data: PropTypes.array,
  combined: PropTypes.bool
};

MultiDataColumn.Link = Link;

export default MultiDataColumn;
